import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  MenuItem,
  Tab,
  Tabs,
  TextField,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import PrintOutlinedIcon from "@material-ui/icons/PrintOutlined";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import AccountBalanceOutlinedIcon from "@material-ui/icons/AccountBalanceOutlined";
import useStyles from "../crm/styles";
import { EmptyState, PageHead, useToast } from "../crm/crmUi";
import { apiError } from "../crm/crmService";
import SchoolService from "../Locality/Service/schoolService";
import AddClassService from "../Locality/Service/addClassService";
import SectionService from "../Locality/Service/sectionService";
import FeeCollectionService, { OFFLINE_MODES, amountInWords, dmy, money, todayYmd } from "./feeCollectionService";
import { ReceiptCard, printReceipt } from "./Receipt";

const TABS = ["Collect fee", "Day report", "Cheques", "Receipts"];
const EMPTY_CHEQUE = { bankName: "", branch: "", chequeNo: "", chequeDate: "" };
const userDetail = () => {
  try {
    return JSON.parse(localStorage.getItem("userDetail")) || {};
  } catch (e) {
    return {};
  }
};

/** School name + address for the live receipt preview (the saved receipt carries its own copy). */
function useSchoolHeader() {
  const [header, setHeader] = useState({ schoolName: "", schoolAddress: "" });
  useEffect(() => {
    const u = userDetail();
    const id = u.schooleId?._id || u.schooleId || u.schoolId?._id || u.schoolId;
    if (!id || u.role === "SUPERADMIN") return;
    SchoolService.getSchooleRegistrationById(id)
      .then((res) => {
        const s = res?.data || res || {};
        setHeader({
          schoolName: s.schooleName || "",
          schoolAddress: [s.address, s.city, s.state, s.pincode].filter(Boolean).join(", "),
        });
      })
      .catch(() => {});
  }, []);
  return header;
}

/* ------------------------------- Collect fee ------------------------------- */

const emptyForm = () => ({
  student: null,
  feeId: "",
  installmentId: "",
  amount: "",
  paymentMode: "Cash",
  cheque: { ...EMPTY_CHEQUE },
  collectedBy: userDetail().name || "",
  remarks: "",
});

function CollectTab({ toast, onSaved }) {
  const classes = useStyles();
  const header = useSchoolHeader();
  const [form, setForm] = useState(emptyForm);
  const [studentOptions, setStudentOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [options, setOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const searchSeq = useRef(0);
  // Narrow the student list first: class, then (optional) section.
  const [classList, setClassList] = useState([]);
  const [sectionList, setSectionList] = useState([]);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setCheque = (patch) => setForm((f) => ({ ...f, cheque: { ...f.cheque, ...patch } }));

  useEffect(() => {
    const u = userDetail();
    if (!u.schooleId) return;
    AddClassService.getAllAddClass(u.schooleId)
      .then((res) => {
        const list = (Array.isArray(res) ? res : []).filter((c) => c && c._id && c.className);
        setClassList(list.sort((a, b) => String(a.className).localeCompare(String(b.className))));
      })
      .catch(() => setClassList([]));
  }, []);

  useEffect(() => {
    setSectionList([]);
    if (!classId) return;
    SectionService.findSectionList({ schooleId: userDetail().schooleId, classId })
      .then((res) => setSectionList(Array.isArray(res) ? res : []))
      .catch(() => setSectionList([]));
  }, [classId]);

  const search = useCallback(
    (q) => {
      const seq = ++searchSeq.current;
      setSearching(true);
      FeeCollectionService.searchStudents(q, { classId, sectionId })
        .then((res) => seq === searchSeq.current && setStudentOptions(Array.isArray(res) ? res : []))
        .catch(() => seq === searchSeq.current && setStudentOptions([]))
        .finally(() => seq === searchSeq.current && setSearching(false));
    },
    [classId, sectionId]
  );
  useEffect(() => search(""), [search]);

  const pickStudent = (student) => {
    set({ student, feeId: "", installmentId: "", amount: "" });
    setOptions(null);
    setErrors({});
    if (!student) return;
    setLoadingOptions(true);
    FeeCollectionService.studentOptions(student._id)
      .then((res) => {
        setOptions(res);
        const first = res?.fees?.[0];
        if (first) set({ feeId: String(first._id) });
      })
      .catch((err) => toast(apiError(err, "Could not load the student's fees.").message, "error"))
      .finally(() => setLoadingOptions(false));
  };

  const fee = options?.fees?.find((f) => String(f._id) === String(form.feeId)) || null;
  // Installments with a cheque still clearing are left out until it is Credited or Returned.
  const dueInstallments = (fee?.installments || []).filter((i) => i.paymentStatus === "Due");

  // The amount always comes from the chosen installment; it can't be typed.
  const pickInstallment = (id) => {
    const inst = dueInstallments.find((i) => String(i._id) === String(id));
    set({ installmentId: id, amount: inst ? String(inst.amount) : "" });
  };

  const isCheque = form.paymentMode === "Cheque";
  const chequeComplete = ["bankName", "branch", "chequeNo", "chequeDate"].every((k) => String(form.cheque[k] || "").trim());
  const canSave = Boolean(form.student && form.installmentId && Number(form.amount) > 0 && (!isCheque || chequeComplete));
  const preview = {
    ...header,
    receiptNo: "",
    receiptDate: todayYmd(),
    source: "offline",
    studentName: form.student?.name || "",
    admissionNumber: form.student?.admissionNumber || "",
    className: options?.student?.className || form.student?.className || "",
    amount: Number(form.amount) || 0,
    amountInWords: amountInWords(form.amount),
    paymentMode: form.paymentMode,
    cheque: isCheque ? form.cheque : null,
    collectedBy: form.collectedBy,
  };

  const save = async () => {
    setSaving(true);
    setErrors({});
    try {
      const res = await FeeCollectionService.collectOffline({
        studentId: form.student?._id,
        feeId: form.feeId || undefined,
        installmentId: form.installmentId || undefined,
        amount: form.amount,
        paymentMode: form.paymentMode,
        cheque: isCheque ? form.cheque : undefined,
        collectedBy: form.collectedBy,
        remarks: form.remarks,
      });
      setSaved(res.receipt);
      onSaved();
      toast(res.message || "Receipt saved.");
    } catch (err) {
      const e = apiError(err, "Could not save the payment.");
      setErrors(e.errors);
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const startOver = () => {
    setSaved(null);
    setForm(emptyForm());
    setOptions(null);
  };

  const fieldErr = (k) => ({ error: Boolean(errors[k]), helperText: errors[k] || undefined });

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, 1fr)", gap: 24, alignItems: "start" }} className="fc-collect">
      <style>{"@media (max-width: 960px){.fc-collect{grid-template-columns:1fr !important}}"}</style>
      <div>
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Student</h3>
          <div className={classes.grid2} style={{ marginBottom: 14 }}>
            <TextField
              select
              label="Class"
              value={classId}
              onChange={(e) => {
                setClassId(e.target.value);
                setSectionId("");
                pickStudent(null);
              }}
              variant="outlined"
              size="small"
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">All classes</MenuItem>
              {classList.map((c) => (
                <MenuItem key={c._id} value={String(c._id)}>
                  {c.className}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Section (optional)"
              value={sectionId}
              onChange={(e) => {
                setSectionId(e.target.value);
                pickStudent(null);
              }}
              variant="outlined"
              size="small"
              disabled={!classId || !sectionList.length}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              helperText={classId && !sectionList.length ? "No sections in this class" : " "}
            >
              <MenuItem value="">All sections</MenuItem>
              {sectionList.map((s) => (
                <MenuItem key={s._id} value={String(s._id)}>
                  {s.sectionName}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <Autocomplete
            options={studentOptions}
            value={form.student}
            loading={searching}
            onChange={(_, v) => pickStudent(v)}
            onInputChange={(_, q, reason) => reason === "input" && search(q)}
            getOptionLabel={(o) => (o ? `${o.name}${o.admissionNumber ? ` (${o.admissionNumber})` : ""}` : "")}
            getOptionSelected={(o, v) => String(o._id) === String(v._id)}
            filterOptions={(x) => x}
            renderOption={(o) => (
              <div>
                <div style={{ fontWeight: 500 }}>{o.name}</div>
                <div className={classes.muted}>{[o.admissionNumber, o.className].filter(Boolean).join(" · ")}</div>
              </div>
            )}
            noOptionsText={classId ? "No students in this class / section" : "No students found"}
            renderInput={(params) => (
              <TextField
                {...params}
                label={classId ? `Select student (${studentOptions.length} in class)` : "Search student by name or admission no."}
                variant="outlined"
                size="small"
                {...fieldErr("studentId")}
              />
            )}
          />
          {loadingOptions && <LinearProgress style={{ marginTop: 8 }} />}
          {form.student && options && (
            <div className={classes.grid2} style={{ marginTop: 14 }}>
              {options.fees.length > 1 ? (
                <TextField select label="Fee (academic year)" value={form.feeId} onChange={(e) => set({ feeId: e.target.value, installmentId: "", amount: "" })} variant="outlined" size="small">
                  {options.fees.map((f) => (
                    <MenuItem key={f._id} value={String(f._id)}>
                      {f.academicYear || "Fee"} · {money(f.totalFee)}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField label="Total fee" value={fee ? `${money(fee.totalFee)}${fee.academicYear ? ` · ${fee.academicYear}` : ""}` : "Not set up"} variant="outlined" size="small" InputProps={{ readOnly: true }} />
              )}
              {!options.fees.length && (
                <div className={`${classes.full} ${classes.muted}`}>
                  No fee is set up for this student yet.{" "}
                  <RouterLink to="/app/fee" style={{ color: "#0b5cc2" }}>Set up fees</RouterLink>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Payment</h3>
          <div className={classes.grid2}>
            <TextField
              select
              label="Select installment"
              required
              value={form.installmentId}
              onChange={(e) => pickInstallment(e.target.value)}
              variant="outlined"
              size="small"
              disabled={!dueInstallments.length}
              helperText={
                errors.installmentId ||
                (form.student && options && !dueInstallments.length ? "No installments due for this student" : "Saving marks it Paid")
              }
              error={Boolean(errors.installmentId)}
            >
              {dueInstallments.map((i) => (
                <MenuItem key={i._id} value={String(i._id)}>
                  {money(i.amount)} · due {dmy(i.dueDate)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Amount received (₹)"
              value={form.amount ? money(form.amount) : ""}
              placeholder="From the installment"
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              style={{ background: "#f5f5f5", borderRadius: 4 }}
              error={Boolean(errors.amount)}
              helperText={errors.amount || amountInWords(form.amount) || " "}
            />
          </div>
          <div style={{ marginTop: 14 }}>
            <span className={classes.fieldLabel}>Payment mode</span>
            <div className={classes.chips} role="radiogroup" aria-label="Payment mode">
              {OFFLINE_MODES.map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={form.paymentMode === m}
                  className={`${classes.chip} ${form.paymentMode === m ? classes.chipOn : ""}`}
                  onClick={() => set({ paymentMode: m })}
                >
                  {m}
                </button>
              ))}
            </div>
            {errors.paymentMode && <div className={classes.error}>{errors.paymentMode}</div>}
          </div>
          {isCheque && (
            <div className={classes.grid2} style={{ marginTop: 14, padding: 14, background: "#f5f5f5", borderRadius: 12 }}>
              <TextField label="Bank name" required value={form.cheque.bankName} onChange={(e) => setCheque({ bankName: e.target.value })} variant="outlined" size="small" style={{ background: "#fff" }} {...fieldErr("bankName")} />
              <TextField label="Branch" required value={form.cheque.branch} onChange={(e) => setCheque({ branch: e.target.value })} variant="outlined" size="small" style={{ background: "#fff" }} {...fieldErr("branch")} />
              <TextField label="Cheque no." required value={form.cheque.chequeNo} onChange={(e) => setCheque({ chequeNo: e.target.value })} variant="outlined" size="small" style={{ background: "#fff" }} {...fieldErr("chequeNo")} />
              <TextField label="Cheque dated" required type="date" value={form.cheque.chequeDate} onChange={(e) => setCheque({ chequeDate: e.target.value })} variant="outlined" size="small" InputLabelProps={{ shrink: true }} style={{ background: "#fff" }} {...fieldErr("chequeDate")} />
              {!chequeComplete && (
                <div className={`${classes.full} ${classes.error}`} style={{ marginTop: 0 }}>Fill in bank name, branch, cheque no. and cheque date to save.</div>
              )}
              <div className={`${classes.full} ${classes.muted}`}>The cheque starts as Pending. Mark it Credited or Returned on the Cheques tab.</div>
            </div>
          )}
        </div>

        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Office</h3>
          <div className={classes.grid2}>
            <TextField label="Collected by" value={form.collectedBy} onChange={(e) => set({ collectedBy: e.target.value })} variant="outlined" size="small" />
            <TextField label="Remarks (optional)" value={form.remarks} onChange={(e) => set({ remarks: e.target.value })} variant="outlined" size="small" />
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="outlined" onClick={startOver} disabled={saving}>
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={save}
            disabled={saving || !canSave}
            startIcon={saving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : <ReceiptOutlinedIcon />}
          >
            Save and create receipt
          </Button>
        </div>
      </div>

      <div style={{ position: "sticky", top: 16 }}>
        <div className={classes.sectionTitle}>Receipt preview</div>
        <ReceiptCard receipt={preview} draft />
      </div>

      <Dialog open={Boolean(saved)} onClose={startOver} maxWidth="xs" fullWidth classes={{ paper: classes.dialogPaper }}>
        <DialogContent style={{ padding: 20 }}>{saved && <ReceiptCard receipt={saved} />}</DialogContent>
        <DialogActions style={{ padding: "0 20px 16px" }}>
          <Button variant="outlined" startIcon={<PrintOutlinedIcon />} onClick={() => printReceipt(saved)}>
            Print / Download
          </Button>
          <Button variant="contained" color="primary" onClick={startOver}>
            Collect another
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

/* ------------------------------- Day report -------------------------------- */

function ModeTable({ title, data }) {
  const classes = useStyles();
  const max = Math.max(1, ...data.byMode.map((m) => m.amount));
  return (
    <div className={classes.panelTight}>
      <div className={classes.toolbar}>
        <span className={classes.count}>{title}</span>
        <span className={classes.muted}>{data.count} payments</span>
      </div>
      <table className={classes.table} style={{ minWidth: 0 }}>
        <thead>
          <tr>
            <th>Mode</th>
            <th style={{ textAlign: "right" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {data.byMode.map((m) => (
            <tr key={m.mode} style={{ cursor: "default" }}>
              <td>
                <div>{m.mode}</div>
                <div style={{ height: 6, background: "#ececec", borderRadius: 3, marginTop: 6 }}>
                  <div style={{ width: `${(m.amount / max) * 100}%`, height: 6, background: "#1a1a1a", borderRadius: 3 }} />
                </div>
              </td>
              <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                {money(m.amount)}
                <div className={classes.muted}>{m.count ? `${m.count} paid` : ""}</div>
              </td>
            </tr>
          ))}
          <tr style={{ cursor: "default" }}>
            <td style={{ fontWeight: 700 }}>Total</td>
            <td style={{ textAlign: "right", fontWeight: 700 }}>{money(data.total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function DayReportTab({ report }) {
  const classes = useStyles();
  const c = report.consolidated;
  const tiles = [
    ["Offline", c.offline, "Cash, card, net banking"],
    ["Online", c.online, "UPI, cards, net banking"],
    ["Cheques", c.cheques, "Received today, not returned"],
    ["Total", c.total, "Consolidated for the day"],
  ];
  return (
    <div className={classes.stack}>
      <div className={classes.tiles} style={{ marginBottom: 0 }}>
        {tiles.map(([label, value, note], i) => (
          <div key={label} className={`${classes.tile} ${classes.tileStatic}`} style={i === 3 ? { background: "#1a1a1a", color: "#fff", borderColor: "#1a1a1a" } : undefined}>
            <div className={classes.tileLabel} style={i === 3 ? { color: "#d9d9d9" } : undefined}>
              {label}
            </div>
            <div className={classes.tileValue} style={i === 3 ? { color: "#fff" } : undefined}>
              {money(value)}
            </div>
            <div className={classes.tileNote} style={i === 3 ? { color: "#d9d9d9" } : undefined}>
              {note}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20 }}>
        <ModeTable title="Offline collection" data={report.offline} />
        <ModeTable title="Online collection" data={report.online} />
      </div>
      <div className={classes.panelTight}>
        <div className={classes.toolbar}>
          <span className={classes.count}>Consolidated report</span>
        </div>
        <table className={classes.table} style={{ minWidth: 0 }}>
          <thead>
            <tr>
              <th>Source</th>
              <th style={{ textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {[["Offline", c.offline], ["Online", c.online], ["Cheques", c.cheques]].map(([k, v]) => (
              <tr key={k} style={{ cursor: "default" }}>
                <td>{k}</td>
                <td style={{ textAlign: "right" }}>{money(v)}</td>
              </tr>
            ))}
            <tr style={{ cursor: "default" }}>
              <td style={{ fontWeight: 700 }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: 700 }}>{money(c.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------------------- Cheques --------------------------------- */

const CHEQUE_ORDER = { Returned: 0, Pending: 1, Credited: 2 };

function ChequesTab({ report, onChange, toast, onOpen }) {
  const classes = useStyles();
  const [busy, setBusy] = useState("");
  // Credited cheques are done: they sink to the bottom and can no longer be changed.
  const rows = [...report.cheques].sort((a, b) => (CHEQUE_ORDER[a.chequeStatus] ?? 1) - (CHEQUE_ORDER[b.chequeStatus] ?? 1));

  const setStatus = async (r, status) => {
    if (status === "Credited" && !window.confirm(`Mark cheque ${r.cheque?.chequeNo} as Credited? This can't be changed later.`)) return;
    setBusy(r._id);
    try {
      const res = await FeeCollectionService.setChequeStatus(r._id, status);
      toast(res.message || "Cheque updated.");
      onChange();
    } catch (err) {
      toast(apiError(err, "Could not update the cheque.").message, "error");
    } finally {
      setBusy("");
    }
  };

  if (!rows.length) {
    return <EmptyState icon={<AccountBalanceOutlinedIcon style={{ fontSize: 48 }} />} title="No cheques on this day" text="Cheques collected on the Collect fee tab show here with their status." />;
  }
  return (
    <div className={classes.panelTight}>
      <div className={classes.tableScroll}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>Student name</th>
              <th>Bank</th>
              <th>Branch</th>
              <th>Cheque no.</th>
              <th style={{ textAlign: "right" }}>Amount</th>
              <th>Dated</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const credited = r.chequeStatus === "Credited";
              const returned = r.chequeStatus === "Returned";
              const tone = returned ? { color: "#b3261e" } : credited ? { color: "#8a8a8a" } : undefined;
              return (
                <tr key={r._id} style={{ cursor: "default", background: returned ? "#fdf1f0" : credited ? "#fafafa" : undefined }}>
                  <td style={tone}>
                    <button type="button" onClick={() => onOpen(r)} style={{ background: "none", border: 0, padding: 0, font: "inherit", color: "inherit", cursor: "pointer", textDecoration: "underline" }}>
                      {r.studentName}
                    </button>
                  </td>
                  <td style={tone}>{r.cheque?.bankName}</td>
                  <td style={tone}>{r.cheque?.branch}</td>
                  <td style={tone}>{r.cheque?.chequeNo}</td>
                  <td style={{ ...tone, textAlign: "right" }}>{money(r.amount)}</td>
                  <td style={tone}>{dmy(r.cheque?.chequeDate)}</td>
                  <td>
                    <TextField
                      select
                      value={r.chequeStatus || "Pending"}
                      onChange={(e) => setStatus(r, e.target.value)}
                      variant="outlined"
                      size="small"
                      disabled={credited || busy === r._id}
                      style={{ minWidth: 130 }}
                      inputProps={{ "aria-label": `Status of cheque ${r.cheque?.chequeNo}` }}
                      InputProps={{ style: returned ? { color: "#b3261e", fontWeight: 600 } : undefined }}
                    >
                      {["Pending", "Credited", "Returned"].map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </TextField>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------------------- Receipts -------------------------------- */

function ReceiptsTab({ date, refreshKey, onOpen }) {
  const classes = useStyles();
  const [source, setSource] = useState("");
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    FeeCollectionService.receipts({ date, source: source || undefined })
      .then((res) => setRows(Array.isArray(res) ? res : []))
      .catch(() => setRows([]));
  }, [date, source, refreshKey]);
  return (
    <div className={classes.panelTight}>
      <div className={classes.toolbar}>
        <span className={classes.count}>{rows ? `${rows.length} receipts` : "Receipts"}</span>
        <TextField select value={source} onChange={(e) => setSource(e.target.value)} variant="outlined" size="small" style={{ minWidth: 150 }} SelectProps={{ displayEmpty: true }}>
          <MenuItem value="">All sources</MenuItem>
          <MenuItem value="offline">Offline</MenuItem>
          <MenuItem value="online">Online</MenuItem>
        </TextField>
      </div>
      {!rows && <LinearProgress />}
      {rows && !rows.length && <EmptyState icon={<ReceiptOutlinedIcon style={{ fontSize: 48 }} />} title="No receipts on this day" />}
      {rows && rows.length > 0 && (
        <div className={classes.tableScroll}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Receipt no.</th>
                <th>Student</th>
                <th>Class</th>
                <th>Mode</th>
                <th style={{ textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id} onClick={() => onOpen(r)}>
                  <td>{r.receiptNo}</td>
                  <td>{r.studentName}</td>
                  <td>{r.className}</td>
                  <td>
                    {r.paymentMode}
                    <span className={classes.muted}> · {r.source}</span>
                    {r.chequeStatus === "Returned" && <span style={{ color: "#b3261e" }}> · returned</span>}
                  </td>
                  <td style={{ textAlign: "right" }}>{money(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------- Page ----------------------------------- */

/** Fee collection: collect offline fees with a printable receipt, plus day reports and cheque tracking. */
export default function FeeCollection() {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [date, setDate] = useState(todayYmd());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(null);
  const [toast, toastNode] = useToast();

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (tab === 0) return;
    setLoading(true);
    FeeCollectionService.dayReport(date)
      .then(setReport)
      .catch((err) => toast(apiError(err, "Could not load the day report.").message, "error"))
      .finally(() => setLoading(false));
  }, [tab, date, refreshKey, toast]);

  const pendingCheques = useMemo(() => (report?.cheques || []).filter((c) => c.chequeStatus === "Pending").length, [report]);

  return (
    <div className={classes.page}>
      <PageHead title="Fee collection" subtitle="Collect fees at the office, print receipts, and see what came in each day." />
      <div className={classes.panel}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" indicatorColor="primary" textColor="primary">
            {TABS.map((t) => (
              <Tab
                key={t}
                label={t === "Cheques" && pendingCheques ? `Cheques (${pendingCheques} pending)` : t}
                style={{ textTransform: "none", minWidth: 0, marginRight: 20 }}
              />
            ))}
          </Tabs>
          {tab !== 0 && (
            <TextField
              type="date"
              label="Date"
              value={date}
              onChange={(e) => setDate(e.target.value || todayYmd())}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: todayYmd() }}
            />
          )}
        </div>
        <div style={{ borderTop: "1px solid #c4c4c4", paddingTop: 20 }}>
          {tab === 0 && <CollectTab toast={toast} onSaved={refresh} />}
          {tab !== 0 && loading && !report && <LinearProgress />}
          {tab === 1 && report && <DayReportTab report={report} />}
          {tab === 2 && report && <ChequesTab report={report} onChange={refresh} toast={toast} onOpen={setOpen} />}
          {tab === 3 && <ReceiptsTab date={date} refreshKey={refreshKey} onOpen={setOpen} />}
        </div>
      </div>

      <Dialog open={Boolean(open)} onClose={() => setOpen(null)} maxWidth="xs" fullWidth classes={{ paper: classes.dialogPaper }}>
        <DialogContent style={{ padding: 20 }}>{open && <ReceiptCard receipt={open} />}</DialogContent>
        <DialogActions style={{ padding: "0 20px 16px" }}>
          <Button variant="outlined" startIcon={<PrintOutlinedIcon />} onClick={() => printReceipt(open)}>
            Print / Download
          </Button>
          <Button variant="contained" color="primary" onClick={() => setOpen(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {toastNode}
    </div>
  );
}
